import {ScrollStrategy, ScrollStrategyOptions} from '@angular/cdk/overlay';
import {
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    HostListener,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChange,
    ViewChild
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import * as Models from '@ui-app/models';
import {IDropdownComponent, IDropdownOption, IDropdownOptionId} from './dropdown';


const noop = () => {
    // empty
};

const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => SearchableDropdownComponent), // tslint:disable-line
    multi: true
};

@Component({
    selector: 'ui-searchable-dropdown',
    templateUrl: './dropdown.component.html',
    styleUrls: ['./dropdown.component.less'],
    providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR]
})
export class SearchableDropdownComponent implements ControlValueAccessor, OnInit, OnChanges, IDropdownComponent {

    filterQuery: string = '';
    @Input() scrollStrategy: ScrollStrategy;
    // data
    @Input() keyName = 'key';
    @Input() valueName = 'value';
    // Field which is shown only in dropdown list (can contain more data than valueName field)
    @Input() dropdownValueName = null;
    @Input() dataList: Array<IDropdownOption> = [];
    @Input() enableActions: boolean = false;
    @Input() isSiteDropdown: boolean = false;
    // additional configs
    @Input() disabledOptionIDs?: Array<number | string> = [];
    @Input() placeholderText = 'Select option';
    @Input() noDataText = 'No values have been configured for this field.';
    @Input() allowResetControl = false;
    @Input() homeSiteId: number;
    @Output() inputFocused = new EventEmitter<boolean>(false);
    @Output() public onSelect: EventEmitter<Models.CM.KeyValue> = new EventEmitter<Models.CM.KeyValue>();  // tslint:disable-line
    @ViewChild('inputControl', {}) inputControl: ElementRef;
    optionsList: Array<IDropdownOption> = [];
    selectedOption: IDropdownOption;
    valueText: string;
    isOpened = false;
    isDisabled = false;
    dropdownConfig = {
        filterLimit: 6
    };
    filtersList: Models.CM.KeyValue[] = [];
    @Input() dataFilterConfig: Models.CM.KeyValue;
    // dataFilterConfig.type === 'multi'
    @Input() selectedOptionsList: string[] = [];
    @Input() multiValuePrefix: string;
    @Input() multiValueSuffix: string;
    @Input() optionsWidth: string | number = '100%';
    @Input() isOverlayScrollDisabled: boolean = false;
    private innerSelectedOptionId: IDropdownOptionId; // INTERNAL id
    private prevSelectedOptionId: IDropdownOptionId;
    private onTouchedCallback: () => void = noop;
    private onChangeCallback: (_: any) => void = noop;

    constructor(
        private elementRef: ElementRef,
        private sso: ScrollStrategyOptions
    ) {
    }

    @Input() set currentValue(value: string) {
        if (value) {
            this.valueText = value;
        }
    }

    private get selectedOptionId(): IDropdownOptionId {
        return this.innerSelectedOptionId;
    }

    private set selectedOptionId(optionId: IDropdownOptionId) {
        const isEqualValues: boolean = optionId
            && this.selectedOptionId
            && (optionId.toString() === this.selectedOptionId.toString());

        if (isEqualValues) {
            return;
        }

        // unselect prev selected option(s)
        this.optionsList.forEach((option: IDropdownOption) => option.selected = false);

        try {
            if (!optionId) {
                throw (new Error('Invalid optionId!'));
            }

            const selectedOptionTMP: IDropdownOption = this.optionsList
                .find((option: IDropdownOption) => option[this.keyName].toString() === optionId.toString());

            selectedOptionTMP.selected = true; // mark current selected option (by link)

            this.selectedOption = {...selectedOptionTMP};
            this.valueText = this.selectedOption[this.valueName];
            this.innerSelectedOptionId = optionId;
        } catch (err) {
            this.selectedOption = null;
            this.valueText = '';
            this.innerSelectedOptionId = null;
        }
    }

    @HostListener('document:click', ['$event.target'])
    onClick(target: HTMLElement): void {
        const clickedInside: boolean = this.elementRef.nativeElement.contains(target);
        if (!clickedInside && this.isOpened) {
            this.close('click outside');
        }
    }

    ngOnInit(): void {
        if (this.dataFilterConfig && this.dataFilterConfig.currentKey) {
            this.selectedOptionId = this.dataFilterConfig.currentKey;
        }
        this.setScrollStrategy();
    }

    ngOnChanges(changes: Models.CT.DGuestSimpleChanges<this>): void {
        if (changes.dataList && Array.isArray(changes.dataList?.currentValue)) {
            this.processOptionsListChanges(changes.dataList);
        }
        if (changes.disabledOptionIDs) {
            this.processDisabledOptions();
        }
    }

    // FORM CONTROL IMPLEMENTATION

    writeValue(value: any): void {
        if (value === null) {
            this.selectedOptionId = '';
        }

        /**
         * manual set value option
         */
        if (value && typeof value === 'object' && value.siteName === 'All') {
            this.valueText = value.siteName;
            return;
        }

        // case for filtered options list
        try {
            if (!this.optionsList
                .find((option: IDropdownOption) => option[this.keyName].toString() === value.toString())) {
                return;
            }
        } catch (error) {
            return;
        }

        this.selectedOptionId = value;
        this.prevSelectedOptionId = null;
        // todo: Do I need to close dropdown if the value was set manually (by form from outside)?
        // this.isOpened = false;
        // this.inputControl.nativeElement.focus();
    }

    registerOnChange(fn: (_: any) => void): void {
        this.onChangeCallback = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouchedCallback = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
        this.isOpened = false;
    }

    onFocus(): void {
        this.onTouchedCallback();
    }

    resetSelection(): void {
        this.selectedOptionId = null; // clears innerSelectedOptionId, selectedOption, valueText via the setter
        this.prevSelectedOptionId = null; // ensure re-selecting the same option fires onSelect
        this.filterQuery = '';
        this.isOpened = false;
    }

    /*** View actions ***/

    optionOnClick(option: IDropdownOption, close: boolean = true): void {
        if (option.disabled) {
            return;
        }

        this.setSelectedOption(option[this.keyName]);

        if (close) {
            this.close('optionOnClick');
        }
    }

    toggleState(): void {
        this.isOpened ? this.close('toggleState') : this.open();
    }

    open(): void {
        this.isOpened = true;
    }

    close(calledBy: string): void {
        this.isOpened = false;
        this.inputControl.nativeElement.focus();
    }

    applyMultiselect(data: string[]): void {
        setTimeout(() => {
            this.onSelect.emit(data);
            this.onChangeCallback(data);
            this.isOpened = false;
        });
    }

    showDropdownTitle(): string {
        if (this.selectedOptionsList.length === 1) {
            return this.filtersList.find(item => item.key === Number(this.selectedOptionsList[0])).value;
        } else if (this.selectedOptionsList.length === 0) {
            return `${this.filtersList.length} sites`;
        }
        return `${this.selectedOptionsList.length} sites`;
    }

    setScrollStrategy(): void {
        if (this.isOverlayScrollDisabled) {
            this.scrollStrategy = this.sso.block();
        } else {
            this.scrollStrategy = this.sso.noop();
        }
    }

    /*** Business logic ***/

    private processOptionsListChanges(listChanges: SimpleChange): void {
        if (listChanges.currentValue && Array.isArray(listChanges.currentValue)) {
            const optionsListMapped: IDropdownOption[] = listChanges.currentValue
                // todo: investigate - needs correct deep cloning implementation (@Anton)
                .map((option: IDropdownOption) => {
                    if (typeof option === 'string' || typeof option === 'number') {
                        return {[this.keyName]: option, [this.valueName]: option};
                    } else {
                        return JSON.parse(JSON.stringify(option));
                    }
                });

            this.optionsList = optionsListMapped;

            if (this.dataFilterConfig && this.dataFilterConfig.type === 'multi') {
                this.filtersList = this.optionsList.map(v => ({
                    key: v[this.keyName],
                    value: v[this.valueName]
                })).slice();

                if (this.selectedOptionsList) {
                    if (this.selectedOptionsList.length > 1) {
                        this.valueText = `${this.selectedOptionsList.length} ${this.multiValueSuffix}`;
                    }
                    if (this.selectedOptionsList.length === this.optionsList.length) {
                        this.valueText = 'All';
                    }
                }
            }
        } else {
            this.optionsList = [];
            console.error('DropdownComponent: \'optionsList\' param should be array: ', listChanges.currentValue);
        }
    }

    private processDisabledOptions(): void {
        this.optionsList.forEach((option: IDropdownOption, index: number) => {
            option._index = index;
            option.disabled = this.disabledOptionIDs
                .some((disabledOptionId: IDropdownOptionId) => option[this.keyName] === disabledOptionId);
        });
    }

    private setSelectedOption(optionId: IDropdownOptionId): void {
        this.selectedOptionId = optionId;

        this.emitSelectedValue();
    }

    private emitSelectedValue(): void {
        if (this.selectedOptionId !== this.prevSelectedOptionId || (this.selectedOptionId === null && this.prevSelectedOptionId === null)) {
            const originalOption: Models.CM.KeyValue = {...this.selectedOption};

            delete originalOption.selected;
            delete originalOption.disabled;
            delete originalOption._index;

            this.onSelect.emit(originalOption);

            this.onChangeCallback(this.selectedOptionId);

            this.prevSelectedOptionId = this.selectedOptionId;
        }
    }

}
